<template>
  <fieldset class="CoverageRadioGroup" :class="{ 'v-input--error': fieldValidationErrorMessage }">
    <legend>{{ label }}</legend>
    <div class="options">
      <CoverageRadio
        v-for="coverage in coverages"
        :key="coverage.coverageCode"
        :id="id"
        :text="coverage.title"
        :tooltip="coverage.tooltip"
        :value="coverage.coverageCode"
        :helpText="coverage.shortDescription"
        :selected="coverage.coverageCode === innerValue"
        v-model="innerValue"
        @change="onChange"
      />
      <div
        ref="hiddenFieldWrapper"
        style="display: none"
        aria-hidden="true"
      >
        <v-text-field
          :key="String(innerValue)"
          ref="$field"
          :id="`${id}-hidden`"
          aria-hidden="true"
          :aria-describedby="undefined"
          tabindex="-1"
          v-model="innerValue"
          :rules="validations"
          :hide-details="true"
        />
      </div>
    </div>
    <ErrorMessageComponent :messages="fieldValidationErrorMessage" />
  </fieldset>
</template>
<script setup lang="ts">
  import ErrorMessageComponent from '@/modules/core/components/formFields/ErrorMessageComponent.vue'
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '@/modules/core/composables/accesibilityComposable'
  import { useValidationError } from '@/modules/core/composables/validationErrorsComposable'
  import type { ValidationRuleType } from '@/modules/core/models/types'
  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import CoverageRadio from './CoverageRadio.vue'

  interface PropsInterface {
    id: string
    label?: string
    value?: string | number | null
    coverages: Array<{
      coverageCode: string
      title: string
      tooltip?: string
      shortDescription?: string
    }>
    modelValue?: string | number | null
    rules?: Array<(value: string | number | null) => boolean | string>
  }
  const props = withDefaults(defineProps<PropsInterface>(), {
    modelValue: '',
    value: null,
    rules: () => []
  })
  const emit = defineEmits(['update:modelValue'])
  const { t } = useI18n()

  const innerValue = ref(props.value)
  const validationRules = {
    valid: (value: string) =>
      (!!value && props.coverages.some((elm) => elm.coverageCode === value)) ||
      t('common.validations.option')
  }

  const validations: ValidationRuleType[] = [...props.rules, validationRules.valid]
  const { $field, fieldValidationErrorMessage } = useValidationError()

  function onChange(e: Event) {
    const { value = '' } = e.target as HTMLInputElement
    setValue(value)
  }

  function setValue(value: string | number | null) {
    innerValue.value = value
    emit('update:modelValue', value)
  }

  watch(
    () => props.value,
    (value) => {
      if (value !== props.modelValue) {
        setValue(value ?? null)
      }
    },
    { immediate: true } // * to set default values in Step3HealthConditionsForm
  )

  const hiddenFieldWrapper = ref<HTMLElement | null>(null)
  let hiddenObserver: MutationObserver | null = null

  onMounted(() => {
    hiddenObserver = startDuplicatedLabelControl(hiddenFieldWrapper.value, {
      labelOrphanedInputs: true
    })
  })
  onUnmounted(() => endDuplicatedLabelControl(hiddenObserver))
</script>
<style scoped lang="scss">
  .CoverageRadioGroup {
    border: none;
    margin-top: $spacing-l;

    legend {
      font-family: $font-family-primary;
      font-size: $font-size-m;
      font-style: normal;
      font-weight: $font-weight-400;
      line-height: $line-height-100p;
      color: $color-neutral-dark;
      margin-bottom: $spacing-s;

      &:empty {
        margin: 0;
      }
    }

    .options {
      display: grid;
      gap: $gap-l;
    }

    &.v-input--error {
      :deep(.card) {
        border-color: $color-error;
        background-color: $color-error-200;
      }
    }
  }
</style>
